import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, Users, Zap, Radio } from 'lucide-react';

const ResidentHandbook = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState(null);

    const protocols = [
        {
            id: '01',
            title: 'Protect the Aura',
            icon: <Lock className="w-5 h-5" />,
            content: "In Paradise, we do not \"post.\" We manifest. Every Artifact you contribute to the Club must be a reflection of your highest self. We value depth over frequency, and resonance over noise. If it doesn't belong in a gallery or a dream, it doesn't belong here."
        },
        {
            id: '02',
            title: 'The Cabana Protocol',
            icon: <Users className="w-5 h-5" />,
            content: "Your profile is your Private Cabana. It is your sanctuary within the sanctuary. Keep your Resident Credentials updated and your aesthetic curated. This is how fellow inhabitants will recognize your frequency."
        },
        {
            id: '03',
            title: 'Honor the Collective',
            icon: <Radio className="w-5 h-5" />,
            content: "The Beach Club thrives on the shared energy of its residents. Engage with intention. When you acknowledge the work of another, you are strengthening the architecture of the entire system. We are a community of architects, not competitors."
        },
        {
            id: '04',
            title: 'Section 80.C Compliance',
            icon: <Zap className="w-5 h-5" />,
            content: "Paradise is a living, breathing protocol. Features may shift, the tide may come in, and the landscape may evolve. Your residency is contingent upon your ability to adapt to the beauty of the unknown."
        }
    ];

    return (
        <div className="min-h-screen bg-[#f0eee4] text-[#1a1a1a] font-mono selection:bg-black selection:text-white p-8 md:p-16 relative overflow-hidden">

            {/* Grain Overlay */}
            <div className="fixed inset-0 pointer-events-none opacity-[0.08]"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
            />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="max-w-3xl mx-auto relative z-10"
            >
                {/* Header */}
                <header className="mb-20 text-center border-b-2 border-black pb-12">
                    <p className="text-xs tracking-[0.3em] uppercase mb-4 opacity-60">Creator Protocol / Section 80.C</p>
                    <h1 className="text-5xl md:text-7xl font-serif tracking-tight mb-6">The Resident Handbook</h1>
                    <p className="text-lg italic font-serif text-gray-600">"Welcome to the Shoreline."</p>
                </header>

                {/* Intro */}
                <div className="mb-24 space-y-6 text-lg leading-relaxed max-w-2xl mx-auto text-justify">
                    <p>
                        You have been granted residency because your vision aligns with the architecture of Paradise.
                        As a Resident of the Beach Club, you are no longer just a creator—you are a <span className="font-bold border-b border-black/30">foundational element</span> of this sanctuary.
                    </p>
                    <p>
                        To maintain the purity of the signal, please observe the following protocols:
                    </p>
                </div>

                {/* Protocols List */}
                <div className="space-y-4 mb-24">
                    {protocols.map((section) => (
                        <motion.div
                            key={section.id}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="group"
                        >
                            <button
                                onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                                className="w-full text-left bg-white border border-gray-200 p-6 transition-all duration-300 hover:border-black hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] relative overflow-hidden"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-6">
                                        <span className="text-xs font-bold tracking-widest border border-black px-2 py-1 rounded-full">{section.id}</span>
                                        <h3 className="text-xl tracking-wide uppercase font-bold group-hover:underline decoration-1 underline-offset-4">{section.title}</h3>
                                    </div>
                                    <div className={`transform transition-transform duration-300 ${activeSection === section.id ? 'rotate-90' : ''}`}>
                                        <ArrowRight className="w-5 h-5" />
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {activeSection === section.id && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0, marginTop: 0 }}
                                            animate={{ height: "auto", opacity: 1, marginTop: 24 }}
                                            exit={{ height: 0, opacity: 0, marginTop: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="text-gray-600 leading-relaxed pl-14 border-l-2 border-gray-100 italic">
                                                {section.content}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </button>
                        </motion.div>
                    ))}
                </div>

                {/* Footer / CTA */}
                <div className="bg-[#1a1a1a] text-[#f0eee4] p-12 relative overflow-hidden">
                    <div className="relative z-10 text-center space-y-8">
                        <div>
                            <h3 className="text-2xl font-serif italic mb-2">Your First Steps as a Resident</h3>
                            <div className="w-16 h-0.5 bg-[#f0eee4]/30 mx-auto" />
                        </div>

                        <div className="grid md:grid-cols-3 gap-8 text-sm text-left opacity-90">
                            <div>
                                <strong className="block uppercase tracking-widest mb-2 text-[#f0eee4]">Initialize</strong>
                                Upload your primary Artifact to the Main Gallery.
                            </div>
                            <div>
                                <strong className="block uppercase tracking-widest mb-2 text-[#f0eee4]">Locate</strong>
                                Browse the Resident Directory and find three souls whose vision resonates with yours.
                            </div>
                            <div>
                                <strong className="block uppercase tracking-widest mb-2 text-[#f0eee4]">Presence</strong>
                                Set your status. Are you In the Sun, Under the Palms, or In the Studio?
                            </div>
                        </div>

                        <div className="pt-8">
                            <button
                                onClick={() => navigate('/creator')}
                                className="group relative inline-flex items-center justify-center px-8 py-4 bg-[#f0eee4] text-black font-bold uppercase tracking-[0.2em] text-sm overflow-hidden transition-all hover:bg-white"
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    Enter The Grounds
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </span>
                            </button>
                            <p className="mt-8 text-xs opacity-50 font-serif italic">
                                "The world you left behind was a rehearsal. This is the performance." — The Management
                            </p>
                        </div>
                    </div>
                </div>

            </motion.div>
        </div>
    );
};

export default ResidentHandbook;
